/**
 * /profile — Customer profile page with a left-sidebar tab nav.
 *
 * Tabs:
 *   1. Profile          — Personal info + Financial profile (top 5; show more)
 *   2. Portfolio        — ECAS-based view (placeholder until extractor ships)
 *   3. Term Insurance   — uploaded policies, compact-by-default cards
 *   4. Health Insurance — uploaded policies, compact-by-default cards
 *   …
 *   Billing             — name, GSTIN and address printed on bills
 *   Bills               — invoices, advance receipts and refunds (PDF)
 *
 * Tab state lives in URL search params (?tab=health) so refresh/share keeps
 * the user where they were. `validateSearch` clamps to allowed values.
 */
import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@finmin/auth';
import {
  Badge,
  Button,
  ErrorMessage,
  Skeleton,
} from '@finmin/ui';
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  FileUp,
  HeartPulse,
  Home as HomeIcon,
  Pencil,
  PiggyBank,
  Receipt,
  ReceiptText,
  Shield,
  TrendingUp,
  User as UserIcon,
} from 'lucide-react';
import { useProfile } from '@/api/profile';
import { EditProfileForm } from '@/features/profile/edit-profile-form';
import { DeactivateAccountDialog } from '@/features/profile/components/deactivate-account-dialog';
import { UnmaskControl } from '@/features/unmask';
import {
  ProfileTabSidebar,
  type ProfileTabItem,
} from '@/features/profile-tabs/sidebar';
import { ProfileTab } from '@/features/profile-tabs/profile-tab';
import { PortfolioTab } from '@/features/profile-tabs/portfolio-tab';
import { TermInsuranceTab } from '@/features/profile-tabs/term-tab';
import { HealthInsuranceTab } from '@/features/profile-tabs/health-tab';
import { NpsTab } from '@/features/profile-tabs/nps-tab';
import { HomeLoanTab } from '@/features/profile-tabs/home-loan-tab';
import { UploadTab } from '@/features/profile-tabs/upload-tab';
import { BillingDetailsTab } from '@/features/billing/billing-details-tab';
import { BillsTab } from '@/features/billing/bills-list';

type TabKey =
  | 'profile'
  | 'portfolio'
  | 'term'
  | 'health'
  | 'nps'
  | 'home-loan'
  | 'upload'
  | 'billing'
  | 'bills';
const TAB_KEYS: TabKey[] = [
  'profile',
  'portfolio',
  'term',
  'health',
  'nps',
  'home-loan',
  'upload',
  'billing',
  'bills',
];

export interface ProfileSearch {
  tab: TabKey;
}

export const Route = createFileRoute('/_protected/profile')({
  validateSearch: (search): ProfileSearch => {
    const raw = (search as Record<string, unknown>).tab;
    const tab = typeof raw === 'string' && (TAB_KEYS as string[]).includes(raw)
      ? (raw as TabKey)
      : 'profile';
    return { tab };
  },
  component: ProfilePage,
});

function ProfilePage() {
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: '/profile' });
  const { user } = useAuth();
  const { data: profile, isLoading, error } = useProfile();
  const [editing, setEditing] = React.useState(false);
  const [deactivateOpen, setDeactivateOpen] = React.useState(false);

  const tabs: ProfileTabItem<TabKey>[] = [
    {
      key: 'profile',
      label: 'Profile',
      icon: <UserIcon className="h-4 w-4" aria-hidden />,
    },
    {
      key: 'portfolio',
      label: 'Portfolio',
      icon: <TrendingUp className="h-4 w-4" aria-hidden />,
    },
    {
      key: 'term',
      label: 'Term Insurance',
      icon: <Shield className="h-4 w-4" aria-hidden />,
    },
    {
      key: 'health',
      label: 'Health Insurance',
      icon: <HeartPulse className="h-4 w-4" aria-hidden />,
    },
    {
      key: 'nps',
      label: 'NPS',
      icon: <PiggyBank className="h-4 w-4" aria-hidden />,
    },
    {
      key: 'home-loan',
      label: 'Home Loan',
      icon: <HomeIcon className="h-4 w-4" aria-hidden />,
    },
    {
      key: 'upload',
      label: 'Upload Documents',
      icon: <FileUp className="h-4 w-4" aria-hidden />,
    },
    {
      key: 'billing',
      label: 'Billing',
      icon: <ReceiptText className="h-4 w-4" aria-hidden />,
    },
    {
      key: 'bills',
      label: 'Bills',
      icon: <Receipt className="h-4 w-4" aria-hidden />,
    },
  ];

  function setTab(next: TabKey) {
    navigate({ search: { tab: next }, replace: true });
  }

  const displayName = profile?.full_name ?? user?.name ?? user?.mobile_e164 ?? 'User';
  const initials = getInitials(displayName);

  return (
    <main className="mx-auto max-w-5xl px-6 py-10 space-y-8">
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors group"
      >
        <ArrowLeft
          className="h-4 w-4 transition-transform group-hover:-translate-x-0.5"
          aria-hidden
        />
        Back to Dashboard
      </Link>

      {/* ── Profile header ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-5">
        <div
          className="h-16 w-16 rounded-full flex items-center justify-center text-xl font-bold text-white shrink-0 shadow-md"
          style={{ backgroundColor: 'hsl(var(--palette-forest, 176 72% 17%))' }}
          aria-hidden="true"
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          {isLoading ? (
            <>
              <Skeleton className="h-7 w-40 mb-2" />
              <Skeleton className="h-4 w-28" />
            </>
          ) : (
            <>
              <h1 className="font-display font-medium text-2xl tracking-tight text-foreground truncate">
                {displayName}
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {user?.mobile_e164}
              </p>
            </>
          )}
        </div>
        {!isLoading && profile && (
          <div className="shrink-0">
            {profile.isProfileComplete ? (
              <Badge className="gap-1.5 bg-success/10 text-success border-success/20">
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                Profile complete
              </Badge>
            ) : (
              <Badge variant="outline" className="gap-1.5 border-warning text-warning">
                <AlertCircle className="h-3.5 w-3.5" aria-hidden />
                Incomplete
              </Badge>
            )}
          </div>
        )}
      </div>

      <ErrorMessage message={error?.message ?? null} />

      {/* ── Masked-data reveal control (Profile tab only; renders when masking is enabled) ── */}
      {tab === 'profile' && <UnmaskControl />}

      {/* ── Edit toggle ───────────────────────────────────────────────────── */}
      {!isLoading && profile && tab === 'profile' && (
        <div className="flex justify-end">
          {editing ? (
            <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setEditing(true)}
              className="gap-1.5"
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden />
              Edit profile
            </Button>
          )}
        </div>
      )}

      {editing && profile ? (
        <EditProfileForm
          profile={profile}
          onCancel={() => setEditing(false)}
          onSuccess={() => setEditing(false)}
        />
      ) : (
        <div className="grid gap-6 md:grid-cols-[180px_1fr]">
          <ProfileTabSidebar tabs={tabs} active={tab} onChange={setTab} />
          <section>
            {tab === 'profile' && (
              <ProfileTab
                profile={profile}
                isLoading={isLoading}
                fallbackMobile={user?.mobile_e164}
              />
            )}
            {tab === 'portfolio' && <PortfolioTab />}
            {tab === 'term' && <TermInsuranceTab />}
            {tab === 'health' && <HealthInsuranceTab />}
            {tab === 'nps' && <NpsTab />}
            {tab === 'home-loan' && <HomeLoanTab />}
            {tab === 'upload' && <UploadTab />}
            {tab === 'billing' && <BillingDetailsTab />}
            {tab === 'bills' && <BillsTab />}
          </section>
        </div>
      )}

      {/* ── Account deactivation (Profile tab only) ─────────────────────── */}
      {!editing && tab === 'profile' && user?.mobile_e164 && (
        <section className="flex flex-col gap-4 rounded-xl border border-border p-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-sm font-semibold text-foreground">Deactivate account</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Sign out and stop all access to your account. Contact support to restore it later.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDeactivateOpen(true)}
            className="shrink-0 border-destructive text-destructive hover:bg-destructive/10"
          >
            Deactivate account
          </Button>
          <DeactivateAccountDialog
            mobile={user.mobile_e164}
            open={deactivateOpen}
            onOpenChange={setDeactivateOpen}
          />
        </section>
      )}
    </main>
  );
}

function getInitials(name: string): string {
  if (!name || name.startsWith('+')) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0]![0]!.toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}
